import type { TimeEntryAttributes } from "@/src/shared/types/schemaTypes.generated";
import {
  AllocationWithIssue,
  CalendarAllocation,
  CalendarMainLike,
  CalendarTask,
  LogTimeContext,
  LogTimeInput,
  UpdatableAllocation,
} from "@/src/log_time/types/calendar";
import { MILLISECONDS_IN_HOUR } from "@/src/log_time/constants/calendar";

const getTimeValue = (date: Date | number | string): number | null => {
  if (date instanceof Date) return date.getTime();
  if (typeof date === "number") return date;

  const parsedDate = new Date(date);
  const timeValue = parsedDate.getTime();
  return Number.isNaN(timeValue) ? null : timeValue;
};

export const formatDateForModal = (date: Date | number | string | null | undefined): string | null => {
  if (!date) return null;

  const parsedDate = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(parsedDate.getTime())) return null;

  const year = parsedDate.getFullYear();
  const month = String(parsedDate.getMonth() + 1).padStart(2, "0");
  const day = String(parsedDate.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
};

export const getAllocationHours = (allocation: CalendarAllocation | null | undefined): number | null => {
  if (!allocation?.start_date || !allocation.end_date) return null;

  const startTime = getTimeValue(allocation.start_date);
  const endTime = getTimeValue(allocation.end_date);
  if (startTime === null || endTime === null) return null;

  const hours = (endTime - startTime) / MILLISECONDS_IN_HOUR;
  return Number.isNaN(hours) ? null : hours;
};

export function updateAllocationAfterSubmit(
  main: CalendarMainLike,
  allocation: CalendarAllocation | null,
  task: CalendarTask,
  hours: number
): boolean {
  const allocationHours = getAllocationHours(allocation);
  if (allocationHours === null || !isUpdatableAllocation(allocation, hours, allocationHours)) return false;

  task.spent_hours = (Number.parseFloat(String(task.spent_hours)) || 0) + hours;
  main.tasksView.refreshAll();

  if (allocationHours <= hours) {
    main.scheduler.deleteEvent(allocation.id);
    return true;
  }

  allocation.start_date.setTime(allocation.start_date.getTime() + hours * MILLISECONDS_IN_HOUR);
  main.scheduler.event_updated(allocation);
  main.scheduler.callEvent("onEventChanged", [allocation.id, allocation]);
  return true;
}

const isCalendarAllocation = (allocationOrIssueId: LogTimeInput): allocationOrIssueId is AllocationWithIssue => {
  return (
    typeof allocationOrIssueId === "object" &&
    allocationOrIssueId !== null &&
    allocationOrIssueId.issue_id !== null &&
    allocationOrIssueId.issue_id !== undefined
  );
};

const isUpdatableAllocation = (
  allocation: CalendarAllocation | null,
  hours: number,
  allocationHours: number
): allocation is UpdatableAllocation => {
  return (
    allocation?.start_date instanceof Date &&
    typeof hours === "number" &&
    !Number.isNaN(hours) &&
    typeof allocationHours === "number" &&
    allocationHours > 0
  );
};

const getLogTimeContext = (main: CalendarMainLike, allocationOrIssueId: LogTimeInput): LogTimeContext | null => {
  if (isCalendarAllocation(allocationOrIssueId)) {
    const task = main.taskData.getTaskById(allocationOrIssueId.issue_id, "issues");
    if (!task) return null;

    return {
      allocation: allocationOrIssueId,
      allocationHours: getAllocationHours(allocationOrIssueId),
      entryStartDate: allocationOrIssueId.start_date ?? main.scheduler._currentDate(),
      task,
    };
  }

  if (typeof allocationOrIssueId === "object") return null;

  const task = main.taskData.getTaskById(allocationOrIssueId, "issues");
  if (!task) return null;

  return {
    allocation: null,
    allocationHours: 0,
    entryStartDate: main.scheduler._currentDate(),
    task,
  };
};

export class LogTime {
  constructor(private readonly main: CalendarMainLike) {}

  openForm(allocationOrIssueId: LogTimeInput): void {
    const context = getLogTimeContext(this.main, allocationOrIssueId);
    if (!context) return;

    const currentUserId = this.main.assigneeData?.currentId;

    window.EasyVue.showModal("log_time", null, {
      issueId: context.task.id,
      userId: currentUserId === null || currentUserId === undefined ? null : String(currentUserId),
      hours: context.allocationHours,
      spentOn: formatDateForModal(context.entryStartDate),
      onCloseCallback: (timeEntryWasChanged: boolean, attributes?: TimeEntryAttributes) => {
        if (!timeEntryWasChanged) return;

        const savedHours = Number.parseFloat(String(attributes?.hours));
        const allocationWasUpdated = updateAllocationAfterSubmit(
          this.main,
          context.allocation,
          context.task,
          savedHours
        );

        if (!allocationWasUpdated) window.location.reload();
      },
    });
  }
}
